import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BookOpen,
  Search,
  Calendar,
  Clock,
  ChevronRight,
  Loader2,
  Filter,
  GraduationCap,
  Building2,
  AlertCircle,
  ClipboardList,
  TrendingUp,
  CheckCircle,
  User,
  Lock,
  History,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useMyClasses, useStudentAcademicYears } from '../hooks/useStudent';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import type { StudentClassInfo, GetStudentClassesQueryDto } from '@/types/student.types';
import { getDayName } from '@/types/student.types';

const MyClassesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, tokens } = useAuthStore();
  const tenantId = user?.tenantId;

  // Years the student can consult (enrolment history + current), newest first
  const { data: studentYears = [], isLoading: isLoadingStudentYears } = useStudentAcademicYears();

  // Filters state — the year survives a round trip to a course detail (?academicYearId=)
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string | undefined>(
    searchParams.get('academicYearId') || undefined
  );
  // 1 | 2 = semester parity within the year, 'all' = every semester (grouped view)
  const [selectedSemester, setSelectedSemester] = useState<number | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingYears, setIsLoadingYears] = useState(true);

  // Build query — fetch ALL semesters for the year, filter/group client-side
  const query: GetStudentClassesQueryDto = {
    academicYearId: selectedAcademicYearId,
  };

  // Fetch classes
  const { data: classes = [], isLoading, error, refetch } = useMyClasses(query);

  // Helper function to determine current semester based on today's date
  const getCurrentSemester = (year: AcademicYear): number | undefined => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if we're in semester 1 using explicit dates
    if (year.semester1StartDate && year.semester1EndDate) {
      const s1Start = new Date(year.semester1StartDate);
      const s1End = new Date(year.semester1EndDate);
      s1Start.setHours(0, 0, 0, 0);
      s1End.setHours(23, 59, 59, 999);

      if (today >= s1Start && today <= s1End) {
        return 1;
      }
    }

    // Check if we're in semester 2 using explicit dates
    if (year.semester2StartDate && year.semester2EndDate) {
      const s2Start = new Date(year.semester2StartDate);
      const s2End = new Date(year.semester2EndDate);
      s2Start.setHours(0, 0, 0, 0);
      s2End.setHours(23, 59, 59, 999);

      if (today >= s2Start && today <= s2End) {
        return 2;
      }
    }

    // Fallback: If semester dates are not configured, infer from academic year dates
    // Assume S1 = first half of academic year, S2 = second half
    if (year.startDate && year.endDate) {
      const yearStart = new Date(year.startDate);
      const yearEnd = new Date(year.endDate);
      yearStart.setHours(0, 0, 0, 0);
      yearEnd.setHours(23, 59, 59, 999);

      // Only proceed if today is within the academic year
      if (today >= yearStart && today <= yearEnd) {
        // Calculate midpoint of the academic year
        const midpoint = new Date(yearStart.getTime() + (yearEnd.getTime() - yearStart.getTime()) / 2);

        if (today < midpoint) {
          return 1;
        } else {
          return 2;
        }
      }
    }

    // Outside any semester window (vacances, inter-semestre, année terminée):
    // fall back to the NEAREST semester so the page always opens on the
    // administration-configured current/most-relevant semester
    const s2End = year.semester2EndDate ? new Date(year.semester2EndDate) : new Date(year.endDate);
    const s1End = year.semester1EndDate ? new Date(year.semester1EndDate) : null;
    if (today > s2End) return 2; // year over -> last semester
    if (s1End && today > s1End) return 2; // between semesters -> upcoming S2
    return 1;
  };

  // Load the full year records (semester dates) — used for the semester default only
  useEffect(() => {
    const loadAcademicYears = async () => {
      if (!tokens?.accessToken) return;

      setIsLoadingYears(true);
      try {
        const years = await fetchAcademicYears(tokens.accessToken);
        setAcademicYears(years);
      } catch (err) {
        console.error('Failed to load academic years:', err);
      } finally {
        setIsLoadingYears(false);
      }
    };

    loadAcademicYears();
  }, [tokens?.accessToken]);

  // Default = the current year (computed server-side), unless the URL already names one
  useEffect(() => {
    if (selectedAcademicYearId || studentYears.length === 0) return;
    const currentYear = studentYears.find((y) => y.isCurrent) ?? studentYears[0];
    setSelectedAcademicYearId(currentYear.id);
  }, [studentYears, selectedAcademicYearId]);

  const selectedYear = useMemo(
    () => studentYears.find((y) => y.id === selectedAcademicYearId),
    [studentYears, selectedAcademicYearId]
  );
  // A past year is consultation only: every course opens read-only
  const isPastYear = selectedYear ? !selectedYear.isCurrent : false;

  // Semester default: the live semester for the current year, everything for a past year
  useEffect(() => {
    if (!selectedYear) return;
    if (!selectedYear.isCurrent) {
      setSelectedSemester('all');
      return;
    }
    const fullYear = academicYears.find((y) => y.id === selectedYear.id);
    const currentSemester = fullYear ? getCurrentSemester(fullYear) : undefined;
    if (currentSemester) {
      setSelectedSemester(currentSemester);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id, selectedYear?.isCurrent, academicYears]);

  // Filter classes by semester toggle + search query
  const filteredClasses = useMemo(() => {
    return classes.filter((cls: StudentClassInfo) => {
      // Semester filter (parity: 1 = S1/S3/S5..., 2 = S2/S4/S6...)
      if (selectedSemester !== 'all' && cls.semester) {
        const parity = ((cls.semester - 1) % 2) + 1;
        if (parity !== selectedSemester) return false;
      }

      // Search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        return (
          cls.moduleElementName.toLowerCase().includes(search) ||
          cls.moduleName.toLowerCase().includes(search) ||
          cls.professorName.toLowerCase().includes(search) ||
          cls.filiereName.toLowerCase().includes(search)
        );
      }

      return true;
    });
  }, [classes, selectedSemester, searchQuery]);

  // Labels for the semester toggle, using the student's ABSOLUTE semesters
  // (a 2ème année sees S3/S4, not S1/S2)
  const semesterLabels = useMemo(() => {
    // The year decides the pair (1ère année: S1/S2, 2ème: S3/S4, 3ème: S5/S6…),
    // never the courses already planned: with only S3 courses the second tab
    // must still read S4, not S2.
    const current = classes.filter((c: StudentClassInfo) => !c.isFormerGroup);
    const niveau = Math.max(0, ...(current.length ? current : classes).map((c: StudentClassInfo) => c.niveau || 0));
    if (niveau > 0) {
      return { 1: `Semestre ${2 * niveau - 1}`, 2: `Semestre ${2 * niveau}` } as Record<number, string>;
    }
    const abs = [...new Set(classes.map((c: StudentClassInfo) => c.semester).filter(Boolean))].sort((a, b) => a - b);
    const odd = abs.find((s) => s % 2 === 1);
    return { 1: odd ? `Semestre ${odd}` : 'Semestre 1', 2: odd ? `Semestre ${odd + 1}` : 'Semestre 2' } as Record<number, string>;
  }, [classes]);

  // Group by absolute semester for the 'Tous' view
  const groupedBySemester = useMemo(() => {
    const groups = new Map<number, StudentClassInfo[]>();
    for (const cls of filteredClasses) {
      const sem = cls.semester || 0;
      if (!groups.has(sem)) groups.set(sem, []);
      groups.get(sem)!.push(cls);
    }
    return [...groups.entries()].sort(([a], [b]) => a - b);
  }, [filteredClasses]);

  // Navigate to class detail
  // A former group's course names its group so the detail opens THAT instance
  // (the element may also be taught in the current group)
  const handleClassClick = (cls: StudentClassInfo) => {
    const url = `/institution/${tenantId}/student/mes-cours/${cls.moduleElementId}`;
    const params = new URLSearchParams();
    if (selectedAcademicYearId) params.set('academicYearId', selectedAcademicYearId);
    if (cls.isFormerGroup) params.set('groupeId', cls.groupeId);
    const qs = params.toString();
    navigate(`${url}${qs ? `?${qs}` : ''}`);
  };

  // Get attendance color
  const getAttendanceColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600 bg-green-50';
    if (rate >= 60) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  // Get grade color
  const getGradeColor = (grade: number | null) => {
    if (grade === null) return 'text-gray-500 bg-gray-100';
    if (grade >= 14) return 'text-green-600 bg-green-50';
    if (grade >= 10) return 'text-blue-600 bg-blue-50';
    return 'text-red-600 bg-red-50';
  };

  // Render loading state
  if (isLoadingYears || isLoadingStudentYears) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mes Cours</h1>
              <p className="mt-1 text-sm text-gray-500">
                Consultez vos cours, notes et présences
              </p>
            </div>

            {/* Stats badge */}
            <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg">
              <BookOpen className="h-5 w-5" />
              <span className="font-medium">{filteredClasses.length} Cours</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un cours ou professeur..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Academic year — current by default, past years open read-only */}
            {studentYears.length > 0 && (
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <select
                  value={selectedAcademicYearId ?? ''}
                  onChange={(e) => setSelectedAcademicYearId(e.target.value || undefined)}
                  aria-label="Année académique"
                  className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {studentYears.map((y) => (
                    <option key={y.id} value={y.id}>
                      {y.name}
                      {y.isCurrent ? ' (En cours)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Semester toggle — defaults to the current semester (live from the
                administration's academic-year parameters) */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              {([1, 2] as const).map((sem) => (
                <button
                  key={sem}
                  onClick={() => setSelectedSemester(sem)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    selectedSemester === sem ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {semesterLabels[sem]}
                </button>
              ))}
              <button
                onClick={() => setSelectedSemester('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedSemester === 'all' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Tous
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Past year: consultation only */}
      {isPastYear && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-2 text-sm text-amber-800">
            <Lock className="h-4 w-4 flex-shrink-0" />
            <span>Consultation d'une année passée — lecture seule</span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 mb-6">
            <AlertCircle className="h-5 w-5" />
            <span>Erreur lors du chargement des cours. {(error as Error).message}</span>
            <button
              onClick={() => refetch()}
              className="ml-auto text-red-700 underline hover:no-underline"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredClasses.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun cours trouvé</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery
                ? 'Essayez de modifier votre recherche'
                : "Vous n'avez aucun cours pour cette période"}
            </p>
          </div>
        )}

        {/* Classes Grid — grouped by semestre */}
        {!isLoading && filteredClasses.length > 0 && (
          <div className="space-y-8">
            {groupedBySemester.map(([sem, semClasses]) => (
              <section key={sem}>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-5 w-5 text-purple-600" />
                  <h2 className="text-base font-semibold text-gray-900">{sem ? `Semestre ${sem}` : 'Autres'}</h2>
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {semClasses.length} cours
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {semClasses.map((cls: StudentClassInfo) => (
              <div
                key={`${cls.groupeId}-${cls.moduleElementId}`}
                onClick={() => handleClassClick(cls)}
                className={`bg-white rounded-xl border p-5 hover:shadow-md transition-all cursor-pointer group ${
                  cls.isFormerGroup
                    ? 'border-amber-200 bg-amber-50/30 hover:border-amber-300'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                {/* Title and Arrow */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {cls.moduleElementName}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">
                      {cls.moduleCode} - {cls.moduleName}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5 group-hover:text-blue-500 transition-colors" />
                </div>

                {/* Professor */}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="truncate">{cls.professorName}</span>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                    <GraduationCap className="h-3.5 w-3.5" />
                    {cls.groupeName}
                  </span>
                  {cls.isFormerGroup && (
                    <span
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full"
                      title="Vous avez quitté ce groupe : notes et présences consultables en lecture seule"
                    >
                      <History className="h-3.5 w-3.5" />
                      Ancien groupe
                    </span>
                  )}
                  <span className="inline-flex items-center px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full">
                    S{cls.semester}
                  </span>
                  {cls.campusName && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                      <Building2 className="h-3 w-3" />
                      {cls.campusName}
                    </span>
                  )}
                </div>

                {/* Stats Row */}
                <div className="flex items-center gap-3 mb-3">
                  {/* Attendance Rate */}
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getAttendanceColor(cls.attendanceRate)}`}>
                    <CheckCircle className="h-3.5 w-3.5" />
                    {cls.attendanceRate}%
                  </span>

                  {/* Grade Average */}
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getGradeColor(cls.gradeAverage)}`}>
                    <TrendingUp className="h-3.5 w-3.5" />
                    {cls.gradeAverage !== null ? `${cls.gradeAverage}/20` : 'N/A'}
                  </span>

                  {/* Pending Assignments */}
                  {cls.pendingAssignments > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-700">
                      <ClipboardList className="h-3.5 w-3.5" />
                      {cls.pendingAssignments} devoir{cls.pendingAssignments > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Schedule */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="truncate">
                    {cls.scheduleInfo.length > 0 ? (
                      <>
                        {cls.scheduleInfo.slice(0, 2).map((schedule, index) => (
                          <span key={index}>
                            {index > 0 && ' • '}
                            {getDayName(schedule.dayOfWeek)} {schedule.startTime}-{schedule.endTime}
                          </span>
                        ))}
                        {cls.scheduleInfo.length > 2 && (
                          <span className="text-gray-400"> +{cls.scheduleInfo.length - 2}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400 italic">Pas d'horaire défini</span>
                    )}
                  </div>
                </div>
              </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyClassesPage;
